import React, { useState, useEffect } from 'react';
import { User, CheckCircle, ArrowRight, Building, Award } from 'lucide-react';
import { RoleProfile } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

export default function RoleProfilePage() {
  const [profiles, setProfiles] = useState<RoleProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/role-profiles`);
      if (!response.ok) {
        throw new Error('Failed to fetch role profiles');
      }
      const data = await response.json();
      setProfiles(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching profiles:', err);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level?: string) => {
    switch (level) {
      case 'L4': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'L5': return 'bg-green-100 text-green-800 border-green-200';
      case 'L6': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'L7': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading role profiles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Profiles</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchProfiles}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <User className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Role Profile</h1>
                <p className="text-sm text-gray-600">Business Program Manager Generalist role details</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {profiles.length} profile{profiles.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Role Profiles */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {profiles.map((profile) => (
            <div key={profile.profile_id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-8">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {profile.role_title || 'Unknown Role'}
                    </h2>
                    <div className="flex items-center space-x-4">
                      {profile.job_level && (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(`L${profile.job_level}`)}`}>
                          <Award className="h-4 w-4 mr-1" />
                          L{profile.job_level}
                        </span>
                      )}
                      {profile.job_family && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Building className="h-4 w-4 mr-1" />
                          <span>{profile.job_family}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {profile.description && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Role Description</h3>
                    <p className="text-gray-700 leading-relaxed">{profile.description}</p>
                  </div>
                )}

                {/* Responsibilities */}
                {profile.responsibilities && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Responsibilities</h3>
                    <div className="space-y-3">
                      {profile.responsibilities.split('\n').map((responsibility, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">{responsibility}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Requirements */}
                {profile.requirements && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Requirements</h3>
                    <div className="space-y-3">
                      {profile.requirements.split('\n').map((requirement, index) => (
                        <div key={index} className="flex items-start">
                          <ArrowRight className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">{requirement}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Career Ladder Info */}
                {profile.career_ladder_info && (
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Career Ladder Information</h3>
                    <p className="text-gray-700 leading-relaxed">{profile.career_ladder_info}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {profiles.length === 0 && (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No role profiles found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No role profile information has been recorded yet.
            </p>
          </div>
        )}
      </div>

      {/* Role Context */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Role Context & Background</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Transition</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Transitioned from technical Product Manager role</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Forced into non-coding role at L5 level</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Moved to Scaled Ops team under new management</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Challenges</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Role restrictions on technical work</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Manager expectations for coding progress</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                  <span className="text-sm text-gray-700">Ethical conflicts with data manipulation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
